import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button, useGetList, useRecordContext, useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  useTheme, Box, Paper,
  PaperProps,
  IconButton,
  Tooltip,
  Typography,
  Stack,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { isEmail } from 'ra-core/dist/cjs/inference/assertions';
import { LIST_DEBOUNCE } from '../../../../config';
import { GrayAutocompleteArrayInputWithUpperLabel } from './GrayAutocompleteArrayInputWithUpperLabel';
import { GrayTextFieldWithUpperLabel } from '../fields/GrayTextFieldWithUpperLabel';
import { BhvkCancelButton } from '../../buttons/ActionButtons';

const CERTIFIED_USER_ID = 'certified_user';

/**
 * To be able to include not only users but also emails and the certified user, we need to have a common type that represents different types of choices.
 * For certified user and emails, ids are arbitrary but still must be included to conform to the type.
 */
type NotifiedPersonChoice =
  | { id: typeof CERTIFIED_USER_ID; type: 'certifiedUser';
    previousStepUserCount?: number; }
  | { id: string; type: 'user'; name: string }
  | { id: string; type: 'email'; email: string };

type NotifiedPeopleInputProps = {
  showCertifiedUserOption?: boolean;
  label: string;
  helperText?: string;
  isRequired?: boolean;
  inDialog?: boolean;
  certifiedUsersFromPreviousStep?: { id: string; label: string }[];
};

export type NotifiedPeopleFormValue = {
  peopleToNotify: string[];
  emails: string[];
  isCertificationHolderNotified?: boolean;
};

type EmailCreateDialogProps = {
  onAddChoice: (choice: NotifiedPersonChoice) => void;
  open: boolean;
  onClose: () => void;
};

const toEmailChoice = (email: string): NotifiedPersonChoice => ({
  id: `email:${email}`,
  type: 'email',
  email,
});

const EmailCreateDialog = ({
  open,
  onAddChoice,
  onClose,
}: EmailCreateDialogProps) => {

  const translate = useTranslate();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleClose = () => {

    setEmail('');
    setError(undefined);
    onClose();

  };

  const handleConfirm = () => {

    const trimmed = email.trim();
    if (!trimmed || !isEmail(trimmed)) {

      setError('general.text.invalid_email');
      return;

    }
    const choice = toEmailChoice(trimmed);
    onAddChoice(choice);
    handleClose();

  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: theme.zIndex.modal + 2 }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {translate('general.text.add_email_title')}
        <IconButton
          onClick={handleClose}
          aria-label={translate('general.text.close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <GrayTextFieldWithUpperLabel
          fullWidth
          autoFocus
          value={email}
          onChange={(event) => {

            setEmail(event.target.value);
            setError(undefined);

          }}
          label={translate('general.fields.email')}
          error={Boolean(error)}
          helperText={false}
          onKeyDown={(event) => {

            if (event.key === 'Enter') {

              event.preventDefault();
              handleConfirm();

            }

          }}
        />
        {error && (
          <span style={{ color: theme.palette.error.main }}>
            {translate(error)}
          </span>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BhvkCancelButton onClick={handleClose} />
        <Button label="ra.action.add" onClick={handleConfirm} variant="contained" size="medium" />
      </DialogActions>
    </Dialog>
  );

};

const NotifiedPersonOptionText = () => {

  const record = useRecordContext<NotifiedPersonChoice>();
  const translate = useTranslate();
  if (!record) return null;
  const label = (() => {

    if (record.type === 'certifiedUser') {

      const base = translate(
        'resources.certification-categories.fields.certified_user',
      );
      const count = record.previousStepUserCount;
      const text = count != null && count > 0 ? `${base} (${count})` : base;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 16 }} />
          {text}
        </Box>
      );

    }

    if (record.type === 'email') {

      return record.email;

    }

    return record.name;

  })();

  if (record.type === 'certifiedUser') {

    return (
      <Box
        sx={{
          display: 'block',
          width: '100%',
          fontStyle: 'italic',
          borderRadius: '3px',
        }}
      >
        {label}
      </Box>
    );

  }
  return <span>{label}</span>;

};

export const NotifiedPeopleInput = ({
  showCertifiedUserOption = false,
  label,
  helperText,
  isRequired,
  inDialog = false,
  certifiedUsersFromPreviousStep = [],
}: NotifiedPeopleInputProps) => {

  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { getValues, setValue, watch } = useFormContext();
  const record = useRecordContext<{ notifiedPeople?: { emails?: string[] } }>();
  const watchedEmails = watch('notifiedPeople.emails') as string[] | undefined;

  useEffect(() => {

    if (!location.state?.focusNotifiedPeople) {

      return;

    }

    requestAnimationFrame(() => {

      inputRef.current?.scrollIntoView({ block: 'center' });
      inputRef.current?.focus();

    });

    navigate(location.pathname, { replace: true, state: {} });

  }, [location.pathname, location.state, navigate]);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const { data: users = [] } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { staleTime: LIST_DEBOUNCE });

  const choices = useMemo(() => {

    const result: NotifiedPersonChoice[] = [];
    const emailList = watchedEmails ?? record?.notifiedPeople?.emails ?? [];

    if (showCertifiedUserOption) {

      result.push({
        id: CERTIFIED_USER_ID,
        type: 'certifiedUser',
        previousStepUserCount: certifiedUsersFromPreviousStep?.length,
      });

    }

    users.forEach((user) => {

      result.push({
        id: String(user.id),
        type: 'user',
        name: user.name ?? user.username ?? String(user.id),
      });

    });

    emailList.forEach((email: string) => {

      const choice = toEmailChoice(email);
      if (!result.some((item) => item.id === choice.id)) {

        result.push(choice);

      }

    });

    return result;

  }, [users, watchedEmails, record?.notifiedPeople?.emails, showCertifiedUserOption, certifiedUsersFromPreviousStep]);

  const choicesRef = useRef(choices);
  choicesRef.current = choices;

  const formatSelection = (peopleIds: string[] | undefined) => {

    const notifiedPeople = getValues('notifiedPeople') ?? {};
    const ids: string[] = [...(peopleIds ?? [])];

    if (showCertifiedUserOption && notifiedPeople.isCertificationHolderNotified) {

      if (!ids.includes(CERTIFIED_USER_ID)) {

        ids.unshift(CERTIFIED_USER_ID);

      }

    }

    (notifiedPeople.emails ?? []).forEach((email: string) => {

      const emailId = `email:${email}`;
      if (!ids.includes(emailId)) {

        ids.push(emailId);

      }

    });

    return ids;

  };

  const parseSelection = (selectedIds: string[] | undefined) => {

    const selectedChoices = (selectedIds ?? [])
      .map((id) => choicesRef.current.find((choice) => choice.id === id))
      .filter((choice): choice is NotifiedPersonChoice => choice !== undefined);

    if (showCertifiedUserOption) {

      setValue(
        'notifiedPeople.isCertificationHolderNotified',
        selectedChoices.some((choice) => choice.type === 'certifiedUser'),
        { shouldDirty: true },
      );

    }

    setValue(
      'notifiedPeople.emails',
      selectedChoices
        .filter((choice): choice is Extract<NotifiedPersonChoice, { type: 'email' }> => choice.type === 'email')
        .map((choice) => choice.email),
      { shouldDirty: true },
    );

    return selectedChoices
      .filter((choice): choice is Extract<NotifiedPersonChoice, { type: 'user' }> => choice.type === 'user')
      .map((choice) => choice.id);

  };

  const handleAddEmailChoice = (choice: NotifiedPersonChoice) => {

    if (choice.type !== 'email') return;

    if (!choicesRef.current.some((c) => c.id === choice.id)) {

      choicesRef.current = [...choicesRef.current, choice];

    }

    const nextSelectedIds = [
      ...new Set([
        ...formatSelection(getValues('notifiedPeople.peopleToNotify')),
        choice.id,
      ]),
    ];

    const nextPeopleIds = parseSelection(nextSelectedIds);
    setValue('notifiedPeople.peopleToNotify', nextPeopleIds, {
      shouldDirty: true,
      shouldValidate: true,
    });

  };

  const validateNotifiedPeople = (
    value: string[] | undefined,
    allValues: { notifiedPeople?: NotifiedPeopleFormValue },
  ) => {

    const notified = allValues?.notifiedPeople;
    const hasUsers = (value?.length ?? 0) > 0;
    const hasEmails = (notified?.emails?.length ?? 0) > 0;
    const hasCertifiedUser = Boolean(notified?.isCertificationHolderNotified);

    if (hasUsers || hasEmails || hasCertifiedUser) {

      return undefined;

    }
    return 'ra.validation.required';

  };

  const getChoiceLabel = (choice: NotifiedPersonChoice) => {

    if (choice.type === 'certifiedUser') {

      const base = translate('resources.certification-categories.fields.certified_user');
      const count = certifiedUsersFromPreviousStep?.length;

      if (count != null && count > 0) {

        return `${base} (${count})`;

      }
      return base;

    }

    if (choice.type === 'email') return choice.email;
    return choice.name;

  };

  const notifiedPersonOptionText = <NotifiedPersonOptionText />;

  const AddEmailPaper = (paperProps: PaperProps) => {

    const { children, ...rest } = paperProps;
    return (
      <Paper {...rest} elevation={0}>
        {children}
        <Button
          label="general.text.add_email"
          onMouseDown={(event) => {

            event.preventDefault();

          }}
          onClick={() => setEmailDialogOpen(true)}
          fullWidth
          sx={{
            justifyContent: 'center',
            textAlign: 'center',
            bgcolor: '#E5F1EA',
            borderRadius: '3px',
            m: '4px',
            width: 'calc(100% - 8px)',
            '&:hover': { bgcolor: '#ACB7B1' },
          }}
        />
      </Paper>
    );

  };

  return (
    <Box>
      <GrayAutocompleteArrayInputWithUpperLabel
        source="notifiedPeople.peopleToNotify"
        forcePopupIcon
        label={label}
        validate={validateNotifiedPeople}
        helperText={helperText}
        choices={choices}
        optionValue="id"
        isRequired={isRequired}
        fullWidth
        optionText={notifiedPersonOptionText}
        inputText={(choice: NotifiedPersonChoice) => getChoiceLabel(choice)}
        matchSuggestion={(filter, choice: NotifiedPersonChoice) => getChoiceLabel(choice)
          .toLowerCase()
          .includes(filter.toLowerCase())}
        format={formatSelection}
        parse={parseSelection}
        renderTags={(value, getTagProps) => (value as NotifiedPersonChoice[]).map((option, index) => {

          const { key, ...tagProps } = getTagProps({ index });
          const chip = (
            <Chip
              {...tagProps}
              key={option.id}
              label={getChoiceLabel(option)}
              size="small"
              sx={{
                maxWidth: 'none',
                '& .MuiChip-label': {
                  overflow: 'visible',
                },
              }}
            />
          );
          if (
            option.type === 'certifiedUser'
            && certifiedUsersFromPreviousStep?.length > 0
          ) {

            return (
              <Tooltip
                key={option.id}
                title={
                  <Stack spacing={0.5}>
                    {certifiedUsersFromPreviousStep.map((u) => (
                      <Typography key={u.id} variant="body2">
                        {u.label}
                      </Typography>
                    ))}
                  </Stack>
                }
              >
                <span>{chip}</span>
              </Tooltip>
            );

          }
          return chip;

        })
        }
        PaperComponent={AddEmailPaper}
        TextFieldProps={{
          id: 'notified-people-input',
          inputRef,
        }}
        componentsProps={{
          popper: {
            sx: { zIndex: theme.zIndex.modal + 1 },
          },
          paper: {
            elevation: 0,
            sx: {
              ...(inDialog
                ? { boxShadow: 'none', border: '1px solid', borderColor: 'divider' }
                : {}),
            },
          },
        }}
      />
      <EmailCreateDialog
        open={emailDialogOpen}
        onAddChoice={handleAddEmailChoice}
        onClose={() => setEmailDialogOpen(false)}
      />
    </Box>
  );

};
